import { CONFLICT, FORBIDDEN } from 'http-status-codes';
import { all, call, put, takeLatest } from 'redux-saga/effects';
import { LIFECYCLE, KEY } from 'redux-pack';

import {
  addBreadcrumb,
  captureException,
  captureMessage,
  configureScope,
} from '@quintoandar/fwp-error-monitoring';
import {
  loginSucceeded,
  refreshProfile,
} from '@quintoandar/bioma-auth/containers/actions';
import { REFRESH_PROFILE } from '@quintoandar/bioma-auth/containers/constants';
import { hasAuthCookie } from '@quintoandar/wat-cookie';
import { checkIsAgent } from 'api/agents/agent';
import { sendOfferLink } from 'api/agents/offer';
import { fetchAgentRating } from 'api/agents/review';
import { fetchHouseUserDetails } from 'api/agents/house';

import {
  fetchAgentDataSuccess,
  offerLinkFailure,
  offerLinkSuccess,
  checkedAgent,
  unauthorizedAgent,
} from 'containers/App/actions';
import {
  FETCH_AGENT_RATING,
  INITIALIZE_AUTH,
  OFFER_LINK_REQUEST,
  LOAD_PROFILE_FROM_CACHE,
  USERS_ME_URL,
} from 'containers/App/constants';

import { AGENT_RATING_DAYS_LIMIT } from 'enums/ratingLimit';
import { getProfileCache, setProfileCache } from 'utils/cache';
import {
  redirectToForbidden,
  redirectToError,
  redirectToProfileConflict,
} from 'utils/sagaUtils';
import { log } from 'utils/logUtils';
import { parseSnapshotToArray } from 'utils';
import { redirectToLoginPage } from 'utils/redirect';

import { fetchUsersDetails } from '../../api/agents/user';

import handleBusinessContext from './business-context-handler';

import {
  getSuccessHouseActionType,
  getIdFromType,
  parseFetchHouseQuery,
  parseFetchUserQuery,
  getSuccessUserActionType,
  mapSearchTypeToRentProgressType,
} from './helpers';

/**
 * Send offer link for tenant
 * @param {object} action
 * @returns {IterableIterator<*>}
 */
export function* sendOfferLinkRequestSaga(action) {
  try {
    yield call(sendOfferLink, { payload: action.payload });
    yield put(offerLinkSuccess());
  } catch (e) {
    yield put(offerLinkFailure(e.message));
  }
}

export function* fetchAgentRatingSaga({ payload }) {
  try {
    addBreadcrumb({
      message: 'Will fetch agent rating',
      category: 'fetch',
      data: { hasAuthCookie: hasAuthCookie() },
    });

    const { hasReviews, rating } = yield call(fetchAgentRating, {
      businessContext: payload?.businessContext,
      days: AGENT_RATING_DAYS_LIMIT,
    });
    const agentData = { hasReviews, rating };
    yield put(fetchAgentDataSuccess({ agentData }));
  } catch (error) {
    captureMessage('Failed to fetch agent rating', {
      extra: {
        error,
      },
    });
  }
}
function* checkActiveAgent() {
  yield call(checkIsAgent);
  yield put(checkedAgent(true));
}
export function* initializeAuthSaga() {
  try {
    const isAuthenticated = yield call(hasAuthCookie);
    if (!isAuthenticated) {
      redirectToLoginPage();
      return;
    }

    // The real user data will come from refreshProfile middleware → /users/me
    const minimalAuthResponse = {
      success: true,
      user_id: 0, // Will be updated by refreshProfile
      user_email: '', // Will be updated by refreshProfile
    };
    yield put(loginSucceeded(minimalAuthResponse));
  } catch (error) {
    captureException('An error has occured while initializeAuthSaga', {
      extra: {
        error,
      },
    });
    log('An error has occured while initializeAuthSaga', {
      error,
    });
  }
}

export function* refreshProfileSuccess(action) {
  if (!action.payload) return;

  const {
    email,
    id,
    name,
    agentId,
    businessContext,
    optedForOnlineSupport,
    roles,
    isProspectChatEnabled,
    isPassiveLeadReceiver,
    accreditationDate,
    isCached,
  } = action.payload;

  const refreshedProfile = agentId !== 0;
  const agentData = {
    businessContext: handleBusinessContext({ businessContext }),
    optedForOnlineSupport,
    roles,
    isProspectChatEnabled,
    isPassiveLeadReceiver,
    accreditationDate,
  };

  try {
    if (isCached) {
      yield put(checkedAgent(true));
    } else {
      yield* checkActiveAgent();
      yield call(setProfileCache, action.payload);
    }

    configureScope((scope) => {
      scope.setUser({
        email,
        id,
        name,
      });
    });
    yield put(fetchAgentDataSuccess({ agentData, refreshedProfile }));
  } catch (error) {
    yield* refreshProfileFailure(action, error);
  }
}

export function* loadProfileFromCache() {
  try {
    const {
      email,
      id,
      name,
      agentId,
      businessContext,
      optedForOnlineSupport,
      roles,
      isProspectChatEnabled,
      isPassiveLeadReceiver,
      accreditationDate,
    } = getProfileCache();

    // Dispatch REFRESH_PROFILE with success lifecycle to trigger middlewares
    yield put({
      type: REFRESH_PROFILE,
      payload: {
        email,
        id,
        name,
        agentId,
        businessContext,
        optedForOnlineSupport,
        roles,
        isProspectChatEnabled,
        isPassiveLeadReceiver,
        accreditationDate,
        isCached: true,
      },
      meta: { [KEY.LIFECYCLE]: LIFECYCLE.SUCCESS },
    });
  } catch (error) {
    log('error in loadProfileFromCache, falling back to refresh profile', {
      error,
    });
    yield put(refreshProfile(USERS_ME_URL));
  }
}

export function* refreshProfileFailure(action, error = null) {
  const isForbidden =
    error?.response?.status === FORBIDDEN ||
    action?.payload?.response?.status === FORBIDDEN;

  const isConflict =
    error?.response?.status === CONFLICT ||
    action?.payload?.response?.status === CONFLICT;

  log('error in refreshProfile', {
    action,
    error,
  });
  if (isForbidden) {
    yield* redirectToForbiddenAndUnauthorized();
  } else if (isConflict) {
    yield* redirectToConflict();
  } else {
    captureException('Error in refreshProfile', {
      extra: {
        error,
        action,
      },
    });
    yield* redirectToErrorAndUnauthorized();
  }
}

export function* redirectToForbiddenAndUnauthorized() {
  yield put(unauthorizedAgent());
  yield* redirectToForbidden();
}

export function* redirectToConflict() {
  yield* redirectToProfileConflict();
}

export function* redirectToErrorAndUnauthorized() {
  yield put(unauthorizedAgent());
  yield* redirectToError();
}

export function* refreshProfileSaga(action) {
  if (action.meta[KEY.LIFECYCLE] === LIFECYCLE.SUCCESS) {
    yield* refreshProfileSuccess(action);
  } else if (action.meta[KEY.LIFECYCLE] === LIFECYCLE.FAILURE) {
    yield* refreshProfileFailure(action);
  }
}

export function* fetchUserData(action, type) {
  const { data: snapshotResult } = action.payload;
  const fieldName = mapSearchTypeToRentProgressType(type);
  const typedKey = getIdFromType(fieldName);

  const queryResults = parseSnapshotToArray(snapshotResult);
  const parsedQueryResults = queryResults.map(parseFetchUserQuery(typedKey));
  const usersId = parsedQueryResults.map((queryResult) => queryResult.tenantId);

  if (usersId.length > 0) {
    const usersDetails = yield call(fetchUsersDetails, { usersId });

    const actionFetchUserSuccess = getSuccessUserActionType(type);

    yield all(
      usersDetails.map((userDetail) => {
        const filteredResult = parsedQueryResults.filter(
          ({ tenantId }) => tenantId === userDetail.id,
        );

        return filteredResult.map((result) => {
          const entityId = result[typedKey];
          const actionPayload = { entityId, ...userDetail };
          return put(actionFetchUserSuccess(actionPayload));
        });
      }),
    );
  }
}

export const fetchUserDataBuilder = (type) => (action) =>
  fetchUserData(action, type);

export function* fetchHouseData(action, type) {
  const { data: snapshotResult, businessContext } = action.payload;
  const fieldName = mapSearchTypeToRentProgressType(type);
  const typedKey = getIdFromType(fieldName);

  const queryResults = parseSnapshotToArray(snapshotResult);
  const houseIds = queryResults.map(({ houseId }) => houseId);
  if (houseIds.length === 0) {
    return;
  }
  const housesDetails = yield call(fetchHouseUserDetails, {
    houseIds,
    businessContext,
  });
  const houses = housesDetails.map((house) => {
    const { property, owner } = house;
    return { ...property, owner };
  });
  const parsedQueryResults = queryResults.map(parseFetchHouseQuery(typedKey));
  const queryResultsWithHouses = parsedQueryResults.map((entity) => {
    const house = houses.find((property) => property.code === entity.houseId);
    return { ...house, entityId: entity[typedKey] };
  });
  const actionFetchHouseSuccess = getSuccessHouseActionType(type);
  yield all(
    queryResultsWithHouses.map((house) => put(actionFetchHouseSuccess(house))),
  );
}

export const fetchHouseDataBuilder = (type) => (action) =>
  fetchHouseData(action, type);

/**
 * Map all sagas used in app container
 * @returns {IterableIterator<*>}
 */
function* appSaga() {
  yield all([
    takeLatest(REFRESH_PROFILE, refreshProfileSaga),
    takeLatest(LOAD_PROFILE_FROM_CACHE, loadProfileFromCache),
    takeLatest(INITIALIZE_AUTH, initializeAuthSaga),
    takeLatest(OFFER_LINK_REQUEST, sendOfferLinkRequestSaga),
    takeLatest(FETCH_AGENT_RATING, fetchAgentRatingSaga),
  ]);
}

export default appSaga;
